import Link from 'next/link';
import { useStoreMap, useUnit } from 'effector-react';
import { RiArrowRightLine, RiBarChartHorizontalLine, RiCheckLine, RiHeartFill, RiHeartLine } from 'react-icons/ri';

import { getCartUnitPrice } from '@/features/cart/cart-data';
import { $cartList, pushToCartList } from '@/features/cart/model';
import { $comparisonList, deleteProductFromCompare, pushToComparisonList } from '@/features/compare/model';
import { $favoriteList, deleteProductFromFavorites, pushhToFavoriteList } from '@/features/favorites/model';

import type { Product } from '@/shared/api/generated/api.generated';
import { EquipmentImage } from '@/shared/components/precision/equipment-image';
import { pagesPath } from '@/shared/config/$path';
import { formatCurrency } from '@/shared/lib/currency';

export const ProductCard = ({ item }: { item: Product }) => {
  const [add, addCompare, addFavorite, removeFavorite, removeCompare] = useUnit([
    pushToCartList,
    pushToComparisonList,
    pushhToFavoriteList,
    deleteProductFromFavorites,
    deleteProductFromCompare,
  ]);
  const inCart = useStoreMap({
    store: $cartList,
    keys: [item.id],
    fn: (list, [id]) => list.some((entry) => entry.product.id === id),
  });
  const favorite = useStoreMap({
    store: $favoriteList,
    keys: [item.id],
    fn: (list, [id]) => list.some((entry) => entry.id === id),
  });
  const comparison = useStoreMap({
    store: $comparisonList,
    keys: [item.id],
    fn: (list, [id]) => list.some((entry) => entry.id === id),
  });
  const categoryId = typeof item.category === 'string' ? item.category : item.category?.id;
  const href = pagesPath.catalog._categoryId(categoryId).products._id(item.id).$url();
  const price = getCartUnitPrice(item);
  const oldPrice = Number(item.price.ruble);
  return (
    <article className="product-card">
      <Link className="product-card__image" href={href}>
        <EquipmentImage src={item.media.images[0]} alt={item.name} fill sizes="(max-width:700px) 45vw, 30vw" />
      </Link>
      <div className="product-card__body">
        <div className="product-card__eyebrow">
          <span>{item.brand || 'ОБОРУДОВАНИЕ'}</span>
          <span className={item.stock > 0 ? 'stock-dot' : ''}>{item.stock > 0 ? 'В наличии' : 'Уточнить наличие'}</span>
        </div>
        <Link href={href}>
          <h2>{item.name}</h2>
        </Link>
        <div className="product-card__specs">
          {Object.entries(item.attributes || {})
            .slice(0, 2)
            .map(([key, value]) => (
              <span key={key}>
                {key}: {String(value)}
              </span>
            ))}
        </div>
        <div className="product-card__bottom">
          <div className="product-card__price">{price === undefined ? 'Цена по запросу' : formatCurrency(price)}</div>
          {price !== undefined && oldPrice > price && (
            <span className="product-card__oldprice">{formatCurrency(oldPrice)}</span>
          )}
          <div className="product-card__actions">
            <button className="add-button" type="button" disabled={inCart} onClick={() => add(item)}>
              {inCart ? 'В корзине' : 'В корзину'}
              {inCart ? <RiCheckLine /> : <RiArrowRightLine />}
            </button>
            <button
              className="icon-button"
              type="button"
              aria-label={`${favorite ? 'Убрать из избранного' : 'В избранное'}: ${item.name}`}
              aria-pressed={favorite}
              onClick={() => (favorite ? removeFavorite(item.id) : addFavorite(item))}
            >
              {favorite ? <RiHeartFill /> : <RiHeartLine />}
            </button>
            <button
              className="icon-button"
              type="button"
              aria-label={`${comparison ? 'Убрать из сравнения' : 'Сравнить'}: ${item.name}`}
              aria-pressed={comparison}
              onClick={() => (comparison ? removeCompare(item.id) : addCompare(item))}
            >
              <RiBarChartHorizontalLine />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};
